import React, { FC } from 'react';
import { BigidBody1 } from '@bigid-ui/components';
import { useForm } from 'react-hook-form';
import { Box, Divider, Stack } from '@mui/material';

import { FormFieldsSection } from '../FormFieldsSection';
import { DynamicFormContainer } from '../../DynamicFormContainer';
import { FormSectionVariantType, NormalizedFormFieldType, LayoutType } from '../../../types/common';
import { DynamicFormReducerActionType } from '../../state';
import { LanguageContainer } from '../../../../state/languageContainer';
import { normalizeFields } from '../../utils';
import { Captcha } from '../Captcha';
import { FormSectionButton } from '../FormSectionButton';
import { useFieldVerificationInForm } from '../../../../hooks/useFieldVerificationInForm';
import { useQueryValues } from '../../hooks/useQueryValues';

type RequestSectionFormComponentPropsType = {
  layout: LayoutType;
  fields: NormalizedFormFieldType[];
  submitButtonText?: string;
  onChange?: (values: Record<string, string>) => void;
  onSubmit: (values: Record<string, string>) => void;
  hideSubmit?: boolean;
  disableSubmit?: boolean;
  captchaShown?: boolean;
  margin?: string;
  allFieldValues: Record<string, string>;
};

const RequestSectionFormComponent: FC<RequestSectionFormComponentPropsType> = ({
  layout,
  fields,
  onChange,
  onSubmit,
  submitButtonText = 'Next',
  hideSubmit,
  disableSubmit,
  captchaShown,
  margin,
  allFieldValues,
}) => {
  const { control, handleSubmit, getValues, trigger } = useForm({
    shouldFocusError: true,
  });

  const { handleFieldVerify, fieldVerificationList } = useFieldVerificationInForm({
    getValues,
    validate: trigger,
  });

  const handleFieldsUpdate = () => {
    const values = getValues();
    onChange?.(values);
  };

  return (
    <>
      <FormFieldsSection
        compact={layout === 'compact'}
        control={control}
        fields={fields}
        onChange={handleFieldsUpdate}
        margin={margin}
        fieldVerificationList={fieldVerificationList}
        verifyField={handleFieldVerify(allFieldValues)}
      />
      {captchaShown && <Captcha />}
      {!hideSubmit && (
        <FormSectionButton
          onClick={handleSubmit(onSubmit)}
          disabled={disableSubmit}
          text={submitButtonText}
          dataAid={`${submitButtonText.toLocaleLowerCase()}-button`}
        />
      )}
    </>
  );
};

type RequestSectionFormPropsType = {
  showTitle?: boolean;
  captchaShown?: boolean;
  onUpdate: (values: Record<string, string>) => void;
  section: FormSectionVariantType;
  submitButtonText?: string;
  onSubmit?: (values: Record<string, string>) => void;
  onBack?: () => void;
  margin?: string;
};

export const RequestSectionForm: FC<RequestSectionFormPropsType> = ({
  showTitle = false,
  captchaShown = false,
  onUpdate,
  section,
  submitButtonText = 'Next',
  onSubmit,
  margin,
}) => {
  const {
    dispatch,
    state: { forms, normalizedSections, layout },
  } = DynamicFormContainer.useContainer();

  const { language, BigIdMeTranslate } = LanguageContainer.useContainer();
  const { queryValues, disabledFields } = useQueryValues();

  if (!forms || !normalizedSections?.[section]) {
    return null;
  }

  const title = normalizedSections[section].translations[language]?.title;

  const fields = normalizedSections[section].fields.map(field =>
    normalizeFields({
      field,
      locale: language,
      defaultValue: forms?.[field.name] || queryValues?.[field.name],
      disabled: !!disabledFields?.[field.name],
      translate: BigIdMeTranslate,
    }),
  );

  const disableSubmit = normalizedSections[section].fields.some(field => field.error?.code);

  const handleSubmit = (values: Record<string, string>) => {
    // only for start request section
    queryValues?.requestorType && section === FormSectionVariantType.START_REQUEST && onUpdate(values);
    // only for request type section
    queryValues?.requestType && section === FormSectionVariantType.REQUEST_TYPE && onUpdate(values);
    dispatch({
      type: DynamicFormReducerActionType.SET_FORM_VALUES,
      payload: {
        values,
      },
    });
    onSubmit?.(values);
  };

  return (
    <>
      {showTitle && title && (
        <Box
          sx={{
            p: {
              xs: '16px 0 0 0',
              sm: '24px 24px 0 24px',
            },
          }}
        >
          <Stack direction="row" justifyContent="space-between">
            <BigidBody1 fontWeight="bold">{title}</BigidBody1>
          </Stack>
          <Divider sx={{ mt: '12px' }} />
        </Box>
      )}
      <RequestSectionFormComponent
        margin={margin}
        layout={layout}
        fields={fields}
        onChange={onUpdate}
        onSubmit={handleSubmit}
        submitButtonText={submitButtonText}
        hideSubmit={!onSubmit}
        disableSubmit={disableSubmit}
        captchaShown={captchaShown}
        allFieldValues={forms}
      />
    </>
  );
};
